import React, { useState, useEffect } from 'react';
import { authAPI, applicantAPI } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { Briefcase, CheckCircle2, Send, Pencil, Phone } from 'lucide-react';
import ChangePasswordCard from '@/components/shared/ChangePasswordCard';
import EmailVerifyCard from '@/components/shared/EmailVerifyCard';
import WhatsAppPhoneVerify from '@/components/shared/WhatsAppPhoneVerify';
import BiometricSettingCard from '@/components/shared/BiometricSettingCard';

export default function ApplicantSettings() {
  const { user, loadUser } = useAuthStore();
  const [workEmail, setWorkEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [editing, setEditing] = useState(false);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  // Phone-change state
  const [isStaffingManaged, setIsStaffingManaged] = useState(false);
  const [phoneEditing, setPhoneEditing] = useState(false);
  const [newPhone, setNewPhone] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await applicantAPI.getProfile();
        if (!cancelled) setIsStaffingManaged(!!p.is_staffing_managed);
      } catch { /* profile may not exist yet */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const verifiedWorkEmail = user?.work_email_verified ? user?.work_email : '';

  const sendOtp = async () => {
    if (!workEmail.trim()) return;
    setBusy(true);
    setMsg('');
    try {
      const r = await authAPI.sendWorkEmailOtp(workEmail.trim());
      setOtpSent(true);
      if (r.email_delivery_failed && r.otp) {
        setMsg(`Email delivery unavailable. Your verification code is: ${r.otp}`);
        setOtp(r.otp);
      } else {
        setMsg('Verification code sent to your work email');
      }
    } catch (err) { setMsg('Error: ' + (err.response?.data?.detail || err.message)); }
    setBusy(false);
  };

  const verifyOtp = async () => {
    setBusy(true);
    setMsg('');
    try {
      await authAPI.verifyWorkEmailOtp(otp);
      setMsg('Work email verified!');
      setOtpSent(false);
      setEditing(false);
      setOtp('');
      setWorkEmail('');
      await loadUser();
    } catch (err) { setMsg('Error: ' + (err.response?.data?.detail || err.message)); }
    setBusy(false);
  };

  const startChange = () => {
    setEditing(true);
    setOtpSent(false);
    setOtp('');
    setWorkEmail('');
    setMsg('');
  };

  return (
    <div data-testid="applicant-settings">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Settings</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Manage your account</p>
      <div className="space-y-6 max-w-2xl">
        <ChangePasswordCard testIdPrefix="applicant-pw" />

        <BiometricSettingCard />

        <EmailVerifyCard testIdPrefix="applicant" />

        {/* Phone Number — locked to WhatsApp-verified value; changeable only via OTP.
            Hidden entirely for staffing-managed applicants (their org owns their phone). */}
        {!isStaffingManaged && (
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5" data-testid="phone-card">
            <h3 className="text-slate-900 dark:text-white font-medium text-sm flex items-center gap-2 mb-1"><Phone className="w-4 h-4" /> Phone Number</h3>
            <p className="text-slate-500 dark:text-zinc-500 text-xs mb-4">Your phone is the login identifier for WhatsApp signup and job-match alerts. Changing it requires a fresh WhatsApp OTP.</p>

            {!phoneEditing ? (
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" data-testid="phone-verified-badge">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {user?.phone || 'Not set'}
                </span>
                <button
                  onClick={() => { setPhoneEditing(true); setNewPhone(''); }}
                  className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-zinc-400 hover:text-slate-700 dark:hover:text-zinc-200"
                  data-testid="change-phone-btn"
                >
                  <Pencil className="w-3 h-3" /> Change phone
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <input
                  type="tel"
                  placeholder="+919876543210"
                  value={newPhone}
                  onChange={e => setNewPhone(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600"
                  data-testid="new-phone-input"
                />
                <WhatsAppPhoneVerify
                  phone={newPhone}
                  mode="change-phone"
                  testIdPrefix="settings-wa"
                  onVerified={async () => {
                    setPhoneEditing(false);
                    setNewPhone('');
                    await loadUser();
                  }}
                />
                <button
                  onClick={() => { setPhoneEditing(false); setNewPhone(''); }}
                  className="text-xs text-slate-500 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300"
                  data-testid="cancel-change-phone-btn"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* Work Email Verification */}
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5" data-testid="work-email-card">
          <h3 className="text-slate-900 dark:text-white font-medium text-sm flex items-center gap-2 mb-1"><Briefcase className="w-4 h-4" /> Work Email</h3>
          <p className="text-slate-500 dark:text-zinc-500 text-xs mb-4">Prove you&apos;re currently employed by verifying a company email address (personal providers like Gmail or Outlook aren&apos;t accepted).</p>

          {msg && <p className={`text-sm mb-3 ${msg.startsWith('Error') ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`} data-testid="work-email-msg">{msg}</p>}

          {verifiedWorkEmail && !editing ? (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" data-testid="work-email-verified-badge">
                <CheckCircle2 className="w-3.5 h-3.5" /> Verified — {verifiedWorkEmail}
              </span>
              <button onClick={startChange} className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-zinc-400 hover:text-slate-700 dark:hover:text-zinc-200" data-testid="change-work-email-btn">
                <Pencil className="w-3 h-3" /> Change work email
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <input
                type="email"
                placeholder="you@yourcompany.com"
                value={workEmail}
                onChange={e => setWorkEmail(e.target.value)}
                disabled={otpSent}
                className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 disabled:opacity-60"
                data-testid="work-email-input"
              />
              {!otpSent ? (
                <div className="flex items-center gap-2">
                  <button onClick={sendOtp} disabled={busy || !workEmail.trim()} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm disabled:opacity-50" data-testid="send-work-otp-btn">
                    <Send className="w-3.5 h-3.5" /> {busy ? 'Sending…' : 'Send code'}
                  </button>
                  {verifiedWorkEmail && (
                    <button onClick={() => setEditing(false)} className="text-xs text-slate-500 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300">Cancel</button>
                  )}
                </div>
              ) : (
                <div className="flex gap-2">
                  <input placeholder="Enter code" value={otp} onChange={e => setOtp(e.target.value)} className="flex-1 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600" data-testid="work-otp-input" />
                  <button onClick={verifyOtp} disabled={busy || !otp} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm disabled:opacity-50" data-testid="verify-work-otp-btn">Verify</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
